import {
  DataHeader,
  DataTable,
  DataValue,
  HighlightedMarksTable,
  MarkInfo,
  MarkType,
  SelectedMarksTable,
} from '@tableau/api-internal-contract-js';
import {
  DataDictionaryPresModel,
  HighlightedDataPresModel,
  SelectionDataPresModel,
  VizDataPresModel,
} from '../../gen-src/preslayer/PresModels';
import { ErrorHelpers } from '../ErrorHelpers';
import { PresLayerToApiEnumMappings as EnumMapping } from './EnumMappings';
import { PresLayerOptionalCompatibility } from './PresLayerOptionalCompatibility';
import { UnderlyingDataConverter } from './UnderlyingDataConverter';

/**
 * Contains logic for converting the active marks on a viz into the API pres models.
 * In the original project, most of this was in the ProcessActiveMarks function
 */
export class ActiveMarksDataConverter {
  public static buildSelectedDataTable(selectedData: SelectionDataPresModel): SelectedMarksTable {
    return {
      data: ActiveMarksDataConverter.buildActiveMarksList(selectedData.dataDictionary, selectedData.vizData),
    };
  }

  public static buildHighlightedDataTable(highlightedData: HighlightedDataPresModel): HighlightedMarksTable {
    return {
      data: ActiveMarksDataConverter.buildActiveMarksList(highlightedData.dataDictionary, highlightedData.vizData),
    };
  }

  private static buildActiveMarksList(dataDictionary: DataDictionaryPresModel, vizData: VizDataPresModel): Array<DataTable> {
    const result = new Array<DataTable>();

    const paneColumnsData = vizData.paneColumnsData;
    if (!paneColumnsData) {
      return result;
    }

    // The data model for the VizDataPresModel is a little strange, so this parsing takes some time to figure out.
    // There can be multiple panes of data, so we must go through them one at a time to create data tables
    for (let i = 0; i < paneColumnsData.paneColumnsList.length; i++) {
      // Need to filter to find all the columns which are in this pane
      const columns = paneColumnsData.vizDataColumns.filter((vizDataColumn) => {
        // We know this column matters for the current pane if this pane's index is in paneIndices
        return vizDataColumn.paneIndices.indexOf(i) > -1;
      });

      // Create a mapping of which index in the data dictionary our columns are in
      const columnIndices = columns.map((vizDataColumn) => {
        // The columnIndex is in the same position as the paneIndex
        const paneIndex = vizDataColumn.paneIndices.indexOf(i);
        const columnIndex = vizDataColumn.columnIndices[paneIndex];
        return columnIndex;
      });

      // Get the data for the pane we are processing
      const vizPane = paneColumnsData.paneColumnsList[i];

      if (vizPane.vizPaneColumns.length !== columns.length) {
        throw ErrorHelpers.presModelParsingError('VizDataPresModel');
      }

      const headers = new Array<DataHeader>();
      const rows = new Array<Array<DataValue>>();

      // Column 0 will be the tuple id column. We can use this as a counter for how many rows we have
      const tupleIds = vizPane.vizPaneColumns[0].tupleIds;
      const marks = new Array<MarkInfo>();
      for (let tupleIndex = 0; tupleIndex < tupleIds.length; tupleIndex++) {
        // TODO Need to revaluate the place of this logic. If it turns out that we need a separate command
        // call to get the marks info, then this logic should not be here but instead in a layer above somewhere
        const mark: MarkInfo = {
          type: MarkType.Bar, // hardcoding this for now
          color: 'red', // hardcoding this for now
          tupleId: tupleIds[tupleIndex],
        };
        marks.push(mark);
        // We will have 1 less value in this row since there's the tupleId column
        const cells = new Array<DataValue>(vizPane.vizPaneColumns.length - 1);

        for (let j = 1 /* skip the first which is tuple ids */; j < vizPane.vizPaneColumns.length; j++) {
          // Next, we need to figure out which column we are reading data for
          const column = columns[j];
          const vizPaneColumnData = vizPane.vizPaneColumns[columnIndices[j]];

          const dataType = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(column.dataType);

          // Define the header when we are processing the first row
          if (tupleIndex === 0) {
            const header: DataHeader = {
              dataType: EnumMapping.dataType.convert(dataType),
              fieldName: PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(column.fn),
              isReferenced: true,
              index: j - 1,
              fieldCaption: PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(column.fieldCaption),
            };

            headers.push(header);
          }

          const value = UnderlyingDataConverter.lookupValueFromDictionary(
            dataDictionary,
            dataType,
            vizPaneColumnData.valueIndices[tupleIndex],
          );

          const aliasValue =
            UnderlyingDataConverter.lookupValueFromDictionaryAsString(
              dataDictionary,
              dataType,
              vizPaneColumnData.aliasIndices[tupleIndex],
            ) || '';

          const formattedValue = aliasValue; // TODO - Figure out how to use the formatStrings

          const dataCell: DataValue = {
            value: value,
            formattedValue: formattedValue,
            aliasedValue: aliasValue,
          };

          // Add our cell to this row
          cells[j - 1] = dataCell;
        }

        rows.push(cells);
      }

      const dataTable: DataTable = {
        dataTable: rows,
        headers: headers,
        marks: marks,
      };

      result.push(dataTable);
    }

    return result;
  }
}
